import { Injectable } from '@angular/core';
import { Observable, throwError, of, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationService } from '../authentication/authentication.service';
import { HttpService } from '../http/http.service';

interface UserConfigRecord {
  userid: string;
  ignored_domains?: string[] | null;
  bc_ignore_default_domain?: boolean;
  bc_domain?: string[] | null;
}

@Injectable({
  providedIn: 'root',
})
export class UserConfigService {
  private readonly _basePath = 'rest/v1/user_config';

  constructor(
    private readonly _auth: AuthenticationService,
    private readonly _http: HttpService
  ) {}

  loadIgnoredDomains(): Observable<string[]> {
    const userId = this._getUserId();
    if (!userId) {
      return throwError(() => new Error('No active session'));
    }

    const url = `${this._basePath}?select=ignored_domains&userid=eq.${userId}&limit=1`;
    return this._http.get(url, true).pipe(
      map((response: UserConfigRecord[] | UserConfigRecord | null) => {
        if (!response) {
          return [];
        }

        const record = Array.isArray(response) ? response[0] : response;
        const rawDomains = record?.ignored_domains ?? [];
        return rawDomains
          .filter((domain): domain is string => typeof domain === 'string' && domain.trim().length > 0)
          .map(domain => domain.toLowerCase());
      })
    );
  }

  saveIgnoredDomains(domains: string[]): Observable<void> {
    const userId = this._getUserId();
    if (!userId) {
      return throwError(() => new Error('No active session'));
    }

    const distinctDomains = Array.from(new Set(domains.map(domain => domain.toLowerCase())));
    const payload = {
      userid: userId,
      ignored_domains: distinctDomains,
    };

    const url = `${this._basePath}?on_conflict=userid`;

    return this._http
      .post(url, payload, true, {
        headers: {
          Prefer: 'resolution=merge-duplicates,return=minimal',
        },
      })
      .pipe(map(() => void 0));
  }

  getUserDefaultDomain(): string | null {
    const userDetail = this._auth.getUserDetail();
    const email = userDetail?.user?.email || (userDetail as any)?.email;
    if (!email) {
      return null;
    }

    const parts = email.split('@');
    if (parts.length !== 2) {
      return null;
    }

    return parts[1].toLowerCase();
  }

  async loadBattleCardDomains(): Promise<{ ignoreDefault: boolean; domains: string[] }> {
    const userId = this._getUserId();
    if (!userId) {
      return { ignoreDefault: false, domains: [] };
    }

    try {
      const url = `${this._basePath}?select=bc_ignore_default_domain,bc_domain&userid=eq.${userId}&limit=1`;
      const response = await firstValueFrom(this._http.get(url, true));
      
      if (!response) {
        return { ignoreDefault: false, domains: [] };
      }

      const record = Array.isArray(response) ? response[0] : response;
      const rawDomains = record?.bc_domain ?? [];
      const domains = rawDomains
        .filter((domain: any): domain is string => typeof domain === 'string' && domain.trim().length > 0)
        .map((domain: string) => domain.toLowerCase());

      return {
        ignoreDefault: record?.bc_ignore_default_domain ?? false,
        domains
      };
    } catch (error) {
      console.error('Error loading battle card domains:', error);
      return { ignoreDefault: false, domains: [] };
    }
  }

  async saveBattleCardDomains(ignoreDefault: boolean, domains: string[]): Promise<void> {
    const userId = this._getUserId();
    if (!userId) {
      throw new Error('No active session');
    }

    const distinctDomains = Array.from(new Set(domains.map(domain => domain.toLowerCase())));
    const payload = {
      userid: userId,
      bc_ignore_default_domain: ignoreDefault,
      bc_domain: distinctDomains,
    };

    const url = `${this._basePath}?on_conflict=userid`;

    await firstValueFrom(
      this._http.post(url, payload, true, {
        headers: {
          Prefer: 'resolution=merge-duplicates,return=minimal',
        },
      })
    );
  }

  private _getUserId(): string | null {
    const userDetail = this._auth.getUserDetail();
    return userDetail?.user?.id || (userDetail as any)?.id || null;
  }
}
